import { Suspense, ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { useFeaturePermission } from '@/hooks/useFeaturePermission';

// Lazy load widget components with auto-retry on stale chunks
// Financial widgets - New separated (Conta Azul style)
const ReceivablesOverviewWidget = lazyWithRetry(() => import('./widgets/ReceivablesOverviewWidget'));
const PayablesOverviewWidget = lazyWithRetry(() => import('./widgets/PayablesOverviewWidget'));
const FinancialAccountsWidget = lazyWithRetry(() => import('./widgets/FinancialAccountsWidget'));
// Financial widgets - Charts
const SalesChartWidget = lazyWithRetry(() => import('./widgets/SalesChartWidget'));
const CashFlowChartWidget = lazyWithRetry(() => import('./widgets/CashFlowChartWidget'));
// Financial widgets - Legacy compact
const FinancialSummaryWidget = lazyWithRetry(() => import('./widgets/FinancialSummaryWidget'));
const ReceivablesTodayWidget = lazyWithRetry(() => import('./widgets/ReceivablesTodayWidget'));
const PayablesTodayWidget = lazyWithRetry(() => import('./widgets/PayablesTodayWidget'));
const OverdueAccountsWidget = lazyWithRetry(() => import('./widgets/OverdueAccountsWidget'));
// Sales widgets
const SalesMetricsWidget = lazyWithRetry(() => import('./widgets/SalesMetricsWidget'));
const MyObjectivesWidget = lazyWithRetry(() => import('./widgets/MyObjectivesWidget'));
const TopProductsWidget = lazyWithRetry(() => import('./widgets/TopProductsWidget'));
// CRM widgets
const CustomerPortfolioWidget = lazyWithRetry(() => import('./widgets/CustomerPortfolioWidget'));
const PositivityWidget = lazyWithRetry(() => import('./widgets/PositivityWidget'));
const CustomerRegistrationPendingWidget = lazyWithRetry(() => import('./widgets/CustomerRegistrationPendingWidget'));
// Tasks & Operations widgets
const TasksOverviewWidget = lazyWithRetry(() => import('./widgets/TasksOverviewWidget'));
const DeliveryStatsWidget = lazyWithRetry(() => import('./widgets/DeliveryStatsWidget'));
const InventoryStatsWidget = lazyWithRetry(() => import('./widgets/InventoryStatsWidget'));
const PurchasesPendingWidget = lazyWithRetry(() => import('./widgets/PurchasesPendingWidget'));
const LostSalesSummaryWidget = lazyWithRetry(() => import('./widgets/LostSalesSummaryWidget'));
const RecentActivityWidget = lazyWithRetry(() => import('./widgets/RecentActivityWidget'));
const DataAuditAlertWidget = lazyWithRetry(() => import('./widgets/DataAuditAlertWidget'));
// Production widgets
const ProductionProjectsWidget = lazyWithRetry(() => import('./widgets/ProductionProjectsWidget'));
const ProductionOrdersPendingWidget = lazyWithRetry(() => import('./widgets/ProductionOrdersPendingWidget'));
const ProducedItemsMonthWidget = lazyWithRetry(() => import('./widgets/ProducedItemsMonthWidget'));
const ProductionEfficiencyWidget = lazyWithRetry(() => import('./widgets/ProductionEfficiencyWidget'));
// DRE / KPI widgets
const DRESummaryWidget = lazyWithRetry(() => import('./widgets/DRESummaryWidget'));
const ExpenseCompositionWidget = lazyWithRetry(() => import('./widgets/ExpenseCompositionWidget'));
const MarginIndicatorsWidget = lazyWithRetry(() => import('./widgets/MarginIndicatorsWidget'));
// Inventory Intelligence widgets
const StockHealthWidget = lazyWithRetry(() => import('./widgets/StockHealthWidget'));
const DeadStockWidget = lazyWithRetry(() => import('./widgets/DeadStockWidget'));
const ABCCurveWidget = lazyWithRetry(() => import('./widgets/ABCCurveWidget'));
const ProductTrendWidget = lazyWithRetry(() => import('./widgets/ProductTrendWidget'));

interface WidgetRendererProps {
  type: string;
}

const WidgetSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="h-8 w-24" />
    <Skeleton className="h-16 w-full" />
  </div>
);

function PermissionGuardedWidget({ featureKey, children }: { featureKey: string; children: ReactNode }) {
  const { hasPermission, isLoading } = useFeaturePermission(featureKey);
  if (isLoading) return <WidgetSkeleton />;
  if (!hasPermission) return null;
  return <>{children}</>;
}

export function WidgetRenderer({ type }: WidgetRendererProps) {
  const renderWidget = () => {
    switch (type) {
      // Financial - New separated widgets
      case 'receivables_overview':
        return <ReceivablesOverviewWidget />;
      case 'payables_overview':
        return <PayablesOverviewWidget />;
      case 'financial_accounts':
        return <FinancialAccountsWidget />;
      // Financial - Legacy compact widgets
      case 'financial_summary':
        return <FinancialSummaryWidget />;
      case 'receivables_today':
        return <ReceivablesTodayWidget />;
      case 'payables_today':
        return <PayablesTodayWidget />;
      case 'overdue_accounts':
        return <OverdueAccountsWidget />;
      case 'sales_chart':
        return <SalesChartWidget />;
      case 'cash_flow_chart':
        return <CashFlowChartWidget />;
      // Sales (permission-guarded)
      case 'sales_metrics':
        return <PermissionGuardedWidget featureKey="sales:view_metrics"><SalesMetricsWidget /></PermissionGuardedWidget>;
      case 'my_objectives':
        return <PermissionGuardedWidget featureKey="sales:manage_goals"><MyObjectivesWidget /></PermissionGuardedWidget>;
      case 'top_products':
        return <TopProductsWidget />;
      case 'customer_portfolio':
        return <CustomerPortfolioWidget />;
      case 'positivity':
        return <PositivityWidget />;
      case 'tasks_overview':
        return <TasksOverviewWidget />;
      case 'delivery_stats':
        return <DeliveryStatsWidget />;
      case 'inventory_stats':
        return <InventoryStatsWidget />;
      case 'purchases_pending':
        return <PurchasesPendingWidget />;
      case 'lost_sales_summary':
        return <LostSalesSummaryWidget />;
      case 'recent_activity':
        return <RecentActivityWidget />;
      case 'data_audit_alert':
        return <DataAuditAlertWidget />;
      case 'production_projects':
        return <ProductionProjectsWidget />;
      case 'production_orders_pending':
        return <ProductionOrdersPendingWidget />;
      case 'produced_items_month':
        return <ProducedItemsMonthWidget />;
      case 'production_efficiency':
        return <ProductionEfficiencyWidget />;
      case 'customer_registration_pending':
        return <CustomerRegistrationPendingWidget />;
      case 'dre_summary':
        return <DRESummaryWidget />;
      case 'expense_composition':
        return <ExpenseCompositionWidget />;
      case 'margin_indicators':
        return <MarginIndicatorsWidget />;
      case 'stock_health':
        return <StockHealthWidget />;
      case 'dead_stock':
        return <DeadStockWidget />;
      case 'abc_curve':
        return <ABCCurveWidget />;
      case 'product_trend':
        return <ProductTrendWidget />;
      default:
        return (
          <div className="text-center py-4 text-muted-foreground">
            Widget não encontrado: {type}
          </div>
        );
    }
  };

  return (
    <Suspense fallback={<WidgetSkeleton />}>
      {renderWidget()}
    </Suspense>
  );
}
